import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaArrowsAltV, FaCheckCircle, FaHandshake } from "react-icons/fa";

const PARTNER_REASONS = [
  {
    id: "01",
    title: "Compliance & Governance Framework",
    shortDesc: "Full alignment with Companies Act, 2013 & Schedule VII compliance.",
    content: (
      <ul className="space-y-2 text-xs md:text-sm text-[#1A1714]/75">
        <li className="flex items-start gap-2">
          <FaCheckCircle className="mt-1 shrink-0 text-[#DA2128]" />
          <span>Full alignment with Companies Act, 2013 – Schedule VII</span>
        </li>
        <li className="flex items-start gap-2">
          <FaCheckCircle className="mt-1 shrink-0 text-[#DA2128]" />
          <span>Transparent governance and reporting mechanisms</span>
        </li>
        <li className="flex items-start gap-2">
          <FaCheckCircle className="mt-1 shrink-0 text-[#DA2128]" />
          <span>Regular monitoring, evaluation, and impact assessment</span>
        </li>
        <li className="flex items-start gap-2">
          <FaCheckCircle className="mt-1 shrink-0 text-[#DA2128]" />
          <span>Strong ethical standards and accountability structures</span>
        </li>
      </ul>
    ),
  },
  {
    id: "02",
    title: "Flexibility & Thematic Breadth",
    shortDesc: "Diverse entry points aligned with corporate CSR goals.",
    content: (
      <p className="text-xs md:text-sm leading-relaxed text-[#1A1714]/75">
        From public health to road safety, from education to environmental action, AFCSR offers multiple thematic entry points aligned with corporate CSR goals.
      </p>
    ),
  },
  {
    id: "03",
    title: "Credibility & Legacy",
    shortDesc: "Backed by ASSOCHAM's institutional trust since 1920.",
    content: (
      <p className="text-xs md:text-sm leading-relaxed text-[#1A1714]/75">
        As the CSR arm of India's premier industry chamber (ASSOCHAM, established in 1920), AFCSR brings institutional trust, governmental linkages, and deep industry connections.
      </p>
    ),
  },
  {
    id: "04",
    title: "Proven Impact",
    shortDesc: "Demonstrated reach across key social sectors with measurable outcomes.",
    content: (
      <p className="text-xs md:text-sm leading-relaxed text-[#1A1714]/75">
        Demonstrated reach across health, education, environment, and inclusion — measurable impact in terms of people reached, infrastructure built, and awareness generated.
      </p>
    ),
  },
  {
    id: "05",
    title: "Cross-Sector Collaboration",
    shortDesc: "Connecting automotive, energy, manufacturing, & services sectors.",
    content: (
      <p className="text-xs md:text-sm leading-relaxed text-[#1A1714]/75">
        Opportunity for corporates from varied sectors (automotive, energy, manufacturing, services and more) to engage meaningfully with social initiatives.
      </p>
    ),
  },
  {
    id: "06",
    title: "Scalability & Sustainability",
    shortDesc: "Structured campaigns ensuring long-term sustainable outcomes.",
    content: (
      <p className="text-xs md:text-sm leading-relaxed text-[#1A1714]/75">
        Through structured campaigns, repeatable models, community engagement, and ongoing monitoring, AFCSR ensures that interventions are not one-off but have sustainable outcomes.
      </p>
    ),
  },
];

export function WhyPartnerAFCSR() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="why-partner" className="relative overflow-hidden bg-[#FAF7F2] py-20 lg:py-28">
      {/* Background Subtle Elements */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 h-96 w-[1000px] bg-gradient-to-b from-[#FEF7F4] to-transparent pointer-events-none" />

      <div className="relative mx-auto max-w-6xl px-5 lg:px-10">
        
        {/* Section Heading */}
        <div className="mx-auto max-w-2xl text-center mb-16">
          <span className="inline-block text-[10px] font-bold uppercase tracking-[0.24em] text-[#DB6400]">
            Strategic Synergy
          </span>
          <h2 className="font-display mt-3 text-3xl font-medium leading-[1.05] tracking-tight text-[#1A1714] md:text-4xl lg:text-[2.6rem]">
            Why Partner with <span className="text-gradient-ember italic">AFCSR?</span>
          </h2>
          <div className="mt-4 h-[3px] w-16 rounded-full bg-gradient-to-r from-[#DA2128] to-[#F59C00] mx-auto" />
        </div>

        {/* List Section Inspired by Image */}
        <div className="divide-y divide-[#EFE9DF] border-t border-b border-[#EFE9DF]">
          {PARTNER_REASONS.map((item, index) => {
            const isOpen = openIndex === index;

            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="group py-5 md:py-6 transition-colors duration-300 hover:bg-[#FEF7F4]/50"
              >
                {/* Header Row */}
                <div
                  onClick={() => toggleAccordion(index)}
                  className="flex cursor-pointer items-center justify-between gap-4 md:gap-8 px-2 select-none"
                >
                  <div className="flex items-center gap-6 md:gap-10">
                    {/* ID (01, 02...) */}
                    {/* <span className="font-display text-sm md:text-base font-bold text-[#DB6400] tracking-wider">
                      {item.id}
                    </span> */}

                    {/* Title */}
                    <h3 className="font-serif text-base md:text-lg lg:text-xl font-medium text-[#1A1714] group-hover:text-[#DA2128] transition-colors">
                      {item.title}
                    </h3>
                  </div>

                  {/* Image Style Toggle Icon Button */}
                  <motion.button
                    type="button"
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className={`flex h-8 w-8 md:h-9 md:w-9 shrink-0 items-center justify-center rounded-lg border transition-all ${
                      isOpen
                        ? "bg-[#DA2128] border-[#DA2128] text-white shadow-md shadow-[#DA2128]/20"
                        : "border-[#EFE9DF] bg-[#F5EFE6] text-[#DA2128] group-hover:bg-[#DA2128] group-hover:border-[#DA2128] group-hover:text-white"
                    }`}
                    aria-label="Toggle content"
                  >
                    <FaArrowsAltV className="h-3.5 w-3.5" />
                  </motion.button>
                </div>

                {/* Animated Dropdown Content */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="pl-[2.75rem] md:pl-[4.25rem] pr-2 pt-4 pb-2">
                        <div className="rounded-2xl border border-[#EFE9DF] bg-white p-5 md:p-6 shadow-sm">
                          {item.content}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}